import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';

const SECTIONS = [
  { id: 'overview', title: 'Overview' },
  { id: 'booking-statuses', title: 'Booking Statuses' },
  { id: 'user-cancellation', title: 'Cancellation by Service Users' },
  { id: 'provider-cancellation', title: 'Cancellation by Care Providers' },
  { id: 'booking-rejection', title: 'Booking Rejection' },
  { id: 'refunds', title: 'Refund Policy' },
  { id: 'no-shows', title: 'No-Show Policy' },
  { id: 'repeated-cancellations', title: 'Repeated Cancellations' },
  { id: 'emergency-cancellations', title: 'Emergency Cancellations' },
  { id: 'disputes', title: 'Disputes and Resolution' },
  { id: 'contact', title: 'Contact Us' },
];

export const CancellationPolicy = () => {
  return (
    <PolicyLayout title="Cancellation and Refund Policy" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="overview">
        <h2>Overview</h2>
        <ul>
          <li>
            This Cancellation and Refund Policy outlines the terms and procedures for cancelling
            bookings and requesting refunds on NursingCareBooking.com.
          </li>
          <li>
            This policy applies to all bookings made through the Platform between Service Users and
            Care Providers.
          </li>
          <li>
            By using the Platform to make or accept bookings, you agree to the terms of this policy.
          </li>
          <li>
            We understand that circumstances change, and we aim to provide a fair and transparent
            process for all parties.
          </li>
        </ul>
      </section>

      <section id="booking-statuses">
        <h2>Booking Statuses</h2>
        <p>
          Each booking on the Platform will have one of the following statuses:
        </p>
        <ul>
          <li>
            <strong>Pending</strong>: The booking request has been submitted and is awaiting the Care
            Provider's response. Either party may cancel at this stage without penalty.
          </li>
          <li>
            <strong>Accepted/Approved</strong>: The Care Provider has confirmed the booking.
            Cancellation terms apply from this point.
          </li>
          <li>
            <strong>Rejected</strong>: The Care Provider has declined the booking request. No further
            action is required from the Service User.
          </li>
          <li>
            <strong>Cancelled</strong>: The booking has been cancelled by either the Service User or
            the Care Provider.
          </li>
          <li>
            <strong>Completed</strong>: The care service has been delivered. The booking is finalised
            and may be reviewed by the Service User.
          </li>
        </ul>
      </section>

      <section id="user-cancellation">
        <h2>Cancellation by Service Users</h2>
        <p>
          Service Users may cancel a booking at any time through the Platform.
        </p>
        <p>
          To cancel, navigate to your bookings page and select the booking you wish to cancel. You
          will be asked to provide a reason for the cancellation.
        </p>
        <ul>
          <li>
            <strong>Pending bookings</strong>: May be cancelled at any time without penalty.
          </li>
          <li>
            <strong>Accepted bookings</strong>: We strongly recommend cancelling at least 24 hours
            before the scheduled appointment to allow the Care Provider to adjust their schedule.
          </li>
        </ul>
        <ul>
          <li>
            Cancellation does not automatically entitle you to a refund. See the Refund Policy
            section below.
          </li>
          <li>A record of the cancellation and the reason provided will be retained.</li>
        </ul>
      </section>

      <section id="provider-cancellation">
        <h2>Cancellation by Care Providers</h2>
        <ul>
          <li>
            Care Providers may cancel accepted bookings, but should do so only in genuine
            circumstances.
          </li>
          <li>Providers are expected to give as much notice as possible when cancelling.</li>
          <li>Service Users will be notified immediately via the Platform when a provider cancels.</li>
        </ul>
        <p>Repeated cancellations by Care Providers may result in:</p>
        <ul>
          <li>A reduction in search ranking visibility</li>
          <li>A warning notice on their profile</li>
          <li>Temporary suspension of their account</li>
          <li>Permanent removal from the Platform</li>
        </ul>
        <ul>
          <li>
            Care Providers who need to cancel due to illness or emergency should contact support to
            ensure the Service User is assisted in finding alternative care.
          </li>
        </ul>
      </section>

      <section id="booking-rejection">
        <h2>Booking Rejection</h2>
        <p>
          Care Providers may reject a pending booking request for legitimate reasons, including:
        </p>
        <ul>
          <li>Scheduling conflicts</li>
          <li>The care requirements fall outside their expertise</li>
          <li>The service location is outside their service radius</li>
          <li>Insufficient information provided</li>
        </ul>
        <ul>
          <li>
            Rejection of a booking request does not constitute a cancellation and carries no
            penalty.
          </li>
          <li>
            Service Users will be notified of the rejection and can search for alternative
            providers.
          </li>
          <li>
            Providers should not reject bookings on discriminatory grounds. Such behaviour may
            result in account suspension.
          </li>
        </ul>
      </section>

      <section id="refunds">
        <h2>Refund Policy</h2>
        <ul>
          <li>
            As NursingCareBooking.com is a marketplace connecting Service Users with Care Providers,
            payment arrangements are made directly between the parties.
          </li>
          <li>
            The Platform does not currently process payments and therefore does not directly handle
            refunds.
          </li>
        </ul>
        <p>
          If you have made a payment directly to a Care Provider and wish to request a refund:
        </p>
        <ul>
          <li>Contact the Care Provider directly to discuss the refund</li>
          <li>
            If you cannot reach a resolution, contact our support team for mediation assistance
          </li>
        </ul>
        <p>
          Refund eligibility may depend on:
        </p>
        <ul>
          <li>The cancellation notice period</li>
          <li>Whether the care service was partially or fully delivered</li>
          <li>The reason for cancellation</li>
          <li>Any agreement made directly between the parties</li>
        </ul>
        <ul>
          <li>
            We encourage both parties to agree on cancellation and refund terms before confirming a
            booking.
          </li>
        </ul>
      </section>

      <section id="no-shows">
        <h2>No-Show Policy</h2>
        <p>
          A "no-show" occurs when either party fails to attend or be available for a confirmed
          booking without prior cancellation.
        </p>
        <h3>Service User No-Show</h3>
        <p>
          If a Service User is not available at the agreed location and time without prior notice,
          the Care Provider may mark the booking as a no-show. The Service User may still be liable
          for payment as agreed with the provider.
        </p>
        <h3>Care Provider No-Show</h3>
        <p>
          If a Care Provider fails to arrive for a confirmed booking without prior notice, the
          Service User should:
        </p>
        <ul>
          <li>Attempt to contact the provider through the Platform</li>
          <li>Report the no-show to our support team</li>
          <li>Leave an honest review reflecting their experience</li>
        </ul>
        <ul>
          <li>
            No-shows are taken seriously and may result in account restrictions for repeat
            offenders.
          </li>
        </ul>
      </section>

      <section id="repeated-cancellations">
        <h2>Repeated Cancellations</h2>
        <p>
          We monitor cancellation patterns to maintain Platform quality and reliability. Users with
          excessive cancellations may be subject to:
        </p>
        <ul>
          <li>A formal warning</li>
          <li>Temporary restriction on making or accepting new bookings</li>
          <li>A review of their account by our team</li>
          <li>In serious cases, account suspension</li>
        </ul>
        <ul>
          <li>
            We assess cancellation patterns on a case-by-case basis, taking into account the
            reasons provided and overall account history.
          </li>
        </ul>
      </section>

      <section id="emergency-cancellations">
        <h2>Emergency Cancellations</h2>
        <p>
          We recognise that emergencies happen and will consider the circumstances when assessing
          cancellations. Emergency situations may include:
        </p>
        <ul>
          <li>Sudden illness of the care recipient or caregiver</li>
          <li>Hospitalisation</li>
          <li>Bereavement</li>
          <li>Severe weather conditions preventing travel</li>
          <li>Government-imposed restrictions</li>
        </ul>
        <ul>
          <li>
            If you need to cancel due to an emergency, please cancel through the Platform and note
            the reason. If possible, also contact our support team.
          </li>
          <li>Emergency cancellations will not count towards any cancellation limits.</li>
        </ul>
      </section>

      <section id="disputes">
        <h2>Disputes and Resolution</h2>
        <ul>
          <li>
            If a dispute arises regarding a cancellation or refund, both parties should first
            attempt to resolve it directly.
          </li>
          <li>
            If direct resolution is not possible, contact our support team at{' '}
            <a href="mailto:Info@nursingcarebooking.com">Info@nursingcarebooking.com</a>.
          </li>
          <li>We will review the circumstances and mediate where possible.</li>
        </ul>
        <p>
          Our mediation is advisory and non-binding. For unresolved disputes, parties may seek
          resolution through:
        </p>
        <ul>
          <li>The relevant healthcare ombudsman</li>
          <li>Consumer mediation services</li>
          <li>The courts (as a last resort)</li>
        </ul>
        <ul>
          <li>Please refer to our Complaints Procedure for the full process.</li>
        </ul>
      </section>

      <section id="contact">
        <h2>Contact Us</h2>
        <ContactBlock
          variant="compact"
          introText="For questions about this policy or to discuss a specific cancellation:"
        />
      </section>
    </PolicyLayout>
  );
};
